import { useRef, useState, useEffect } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { api } from './api';

export default function MediaLibrary() {
  const [media, setMedia] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const fileRef = useRef(null);
  const altRef = useRef(null);
  const captionRef = useRef(null);

  const load = () => api('/admin/media').then((d) => setMedia(d.media)).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const file = fileRef.current?.files?.[0];
    if (!file) { setError('Bitte wähle eine Bilddatei aus.'); return; }
    const form = new FormData();
    form.append('file', file);
    form.append('alt', altRef.current?.value || '');
    form.append('caption', captionRef.current?.value || '');
    setUploading(true);
    try {
      await api('/admin/media', { method: 'POST', body: form });
      setSuccess('Bild hochgeladen und optimiert.');
      fileRef.current.value = '';
      if (altRef.current) altRef.current.value = '';
      if (captionRef.current) captionRef.current.value = '';
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const saveMeta = async (m) => {
    await api(`/admin/media/${m.id}`, { method: 'PUT', body: JSON.stringify({ alt: m.alt, caption: m.caption || '' }) });
    setSuccess('Bildangaben gespeichert.');
  };

  const remove = async (id) => {
    try {
      await api(`/admin/media/${id}`, { method: 'DELETE' });
      setConfirmId(null);
      load();
    } catch (e) {
      setError(e.message);
      setConfirmId(null);
    }
  };

  return (
    <div data-testid="media-library">
      <h1 className="font-serif text-3xl font-bold">Medienbibliothek</h1>

      <form onSubmit={upload} className="mt-6 border border-brand-line bg-brand-cream p-5" data-testid="media-upload-form">
        <h2 className="font-serif text-xl font-bold">Bild hochladen</h2>
        <p className="mt-1 text-sm text-brand-brown-soft">Erlaubt: JPG, PNG, WebP – maximal 5 MB. Das Bild wird geprüft und als optimiertes WebP in drei Grössen gespeichert.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="media-file" className="mb-1 block text-sm font-semibold">Datei</label>
            <input ref={fileRef} id="media-file" type="file" accept="image/jpeg,image/png,image/webp" className="w-full text-sm file:mr-3 file:min-h-11 file:rounded-sm file:border-0 file:bg-brand-olive file:px-4 file:font-semibold file:text-white" data-testid="media-file-input" />
          </div>
          <div>
            <label htmlFor="media-alt" className="mb-1 block text-sm font-semibold">Alt-Text</label>
            <input ref={altRef} id="media-alt" className="min-h-11 w-full rounded-sm border border-brand-brown/25 bg-white px-3 text-sm" data-testid="media-alt-input" />
          </div>
          <div>
            <label htmlFor="media-caption" className="mb-1 block text-sm font-semibold">Bildunterschrift (optional)</label>
            <input ref={captionRef} id="media-caption" className="min-h-11 w-full rounded-sm border border-brand-brown/25 bg-white px-3 text-sm" data-testid="media-caption-input" />
          </div>
        </div>
        <button type="submit" disabled={uploading} className="btn-primary mt-4 disabled:opacity-60" data-testid="media-upload-button">
          <Upload className="h-4 w-4" aria-hidden="true" /> {uploading ? 'Lädt hoch …' : 'Hochladen'}
        </button>
        {error && <p role="alert" className="mt-3 rounded-sm border border-red-700 bg-red-50 p-3 text-sm font-semibold text-red-800" data-testid="media-error">{error}</p>}
        {success && <p role="status" className="mt-3 rounded-sm border border-brand-olive bg-white p-3 text-sm font-semibold text-brand-olive-dark" data-testid="media-success">{success}</p>}
      </form>

      {!media ? (
        <p role="status" className="mt-8 text-brand-brown-soft">Lade Medien …</p>
      ) : media.length === 0 ? (
        <p className="mt-8 text-brand-brown-soft">Noch keine Bilder in der Medienbibliothek.</p>
      ) : (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {media.map((m) => (
            <li key={m.id} className="border border-brand-line bg-brand-cream p-4" data-testid={`media-item-${m.token}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${m.variants['640']}`} alt={m.alt || ''} className="aspect-[4/3] w-full rounded-sm border border-brand-line object-cover" loading="lazy" />
              <p className="mt-2 truncate text-sm font-semibold" title={m.original_name}>{m.original_name}</p>
              <p className="text-xs text-brand-brown-soft">{m.width} × {m.height} px · {(m.size_bytes / 1024).toFixed(0)} KB</p>
              <div className="mt-2">
                <label htmlFor={`alt-${m.id}`} className="text-xs font-semibold">Alt-Text</label>
                <input
                  id={`alt-${m.id}`}
                  value={m.alt}
                  onChange={(e) => setMedia(media.map((x) => x.id === m.id ? { ...x, alt: e.target.value } : x))}
                  className="mt-1 min-h-10 w-full rounded-sm border border-brand-brown/25 bg-white px-2 text-sm"
                  data-testid={`media-alt-${m.token}`}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => saveMeta(m)} className="min-h-10 rounded-sm border border-brand-brown/30 px-3 text-sm font-semibold hover:bg-brand-beige" data-testid={`media-save-${m.token}`}>Speichern</button>
                {confirmId === m.id ? (
                  <span className="inline-flex min-h-10 items-center gap-2 rounded-sm border border-red-700 bg-red-50 px-2 text-xs" role="alert">
                    Löschen?
                    <button type="button" onClick={() => remove(m.id)} className="font-bold text-red-800 underline" data-testid={`media-confirm-delete-${m.token}`}>Ja</button>
                    <button type="button" onClick={() => setConfirmId(null)} className="font-semibold underline">Nein</button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setConfirmId(m.id)} className="inline-flex min-h-10 items-center gap-1 rounded-sm border border-red-700 px-3 text-sm font-semibold text-red-800 hover:bg-red-50" data-testid={`media-delete-${m.token}`}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" /> Löschen
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
